import { useState, useEffect } from "react";
import { Link } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { Megaphone, Eye, MousePointerClick, Plus } from "lucide-react";

type Ad = {
  id: number; headline: string; subtext: string | null; imageUrl: string | null;
  status: string; placements: string[]; towns: string[]; durationWeeks: number;
  pricePence: number; endsAt: string | null; rejectionReason: string | null;
  impressions: number; clicks: number; createdAt: string;
};

const STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  draft: { label: "Draft", cls: "bg-gray-100 text-gray-600" },
  pending_payment: { label: "Awaiting payment", cls: "bg-amber-100 text-amber-700" },
  pending_review: { label: "In review", cls: "bg-amber-100 text-amber-700" },
  active: { label: "Live", cls: "bg-emerald-100 text-emerald-700" },
  paused: { label: "Paused", cls: "bg-gray-100 text-gray-600" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-700" },
  expired: { label: "Ended", cls: "bg-gray-100 text-gray-500" },
};

export default function MyAdverts() {
  useAuth();
  const token = localStorage.getItem("ayrshire_token");
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/ads/mine", { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { ads: [] }).then(d => setAds(d.ads ?? []))
      .catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-3xl mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Megaphone size={22} /> My adverts</h1>
        <Link href="/create-ad"><a className="flex items-center gap-1 bg-emerald-600 text-white px-3 py-2 rounded-lg text-sm font-medium"><Plus size={16} /> New advert</a></Link>
      </div>

      {loading ? <p className="text-gray-500">Loading…</p> : ads.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Megaphone className="mx-auto mb-2" size={32} />
          <p>You haven't run any adverts yet.</p>
          <Link href="/create-ad"><a className="text-emerald-700 font-medium">Create your first advert →</a></Link>
        </div>
      ) : (
        <div className="space-y-3">
          {ads.map(ad => {
            const s = STATUS_LABEL[ad.status] ?? { label: ad.status, cls: "bg-gray-100 text-gray-600" };
            const ctr = ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(1) : "0.0";
            return (
              <div key={ad.id} className="border rounded-xl p-3 flex gap-3">
                {ad.imageUrl ? <img src={ad.imageUrl} alt="" className="w-20 h-20 object-cover rounded-lg shrink-0" /> : <div className="w-20 h-20 bg-gray-100 rounded-lg shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold">{ad.headline}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${s.cls}`}>{s.label}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">{ad.placements.join(", ")}{ad.towns.length ? ` · ${ad.towns.join(", ")}` : " · all Ayrshire"} · {ad.durationWeeks}w{ad.endsAt && ad.status === "active" ? ` · ends ${new Date(ad.endsAt).toLocaleDateString()}` : ""}</div>
                  {ad.rejectionReason && <div className="text-xs text-red-500 mt-0.5">Not approved: {ad.rejectionReason}</div>}
                  <div className="flex gap-4 mt-2 text-sm">
                    <span className="flex items-center gap-1 text-gray-700"><Eye size={14} className="text-gray-400" /> <b>{ad.impressions.toLocaleString()}</b> views</span>
                    <span className="flex items-center gap-1 text-gray-700"><MousePointerClick size={14} className="text-gray-400" /> <b>{ad.clicks.toLocaleString()}</b> clicks</span>
                    <span className="text-gray-400">{ctr}% CTR</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
